/**
 * How deep a report page's matrix goes, and how many rows each depth costs.
 *
 * The schedule drawer needs both to offer "expand rows to <tier>" and to warn
 * when the chosen depth will overrun the PDF's print cap. Counting client-side
 * keeps the stepper instant — the rows are already in memory on the page.
 */

/**
 * Row-hierarchy tiers per report page.
 *
 * MIRRORS azfApi/utils/reportPdf.js PAGE_TIERS — the renderer clamps a saved
 * expandLevel against the same list, so the two must stay in step. A page
 * absent from here has no hierarchy worth stepping through and the drawer
 * simply omits the control.
 */
export const PAGE_TIERS = Object.freeze({
    laborManagement: ['Property', 'Class', 'Department', 'Position'],
    financialPnl: ['Property', 'Account type', 'Department'],
    revenueOverview: ['Property', 'Revenue type'],
});

/**
 * How many rows each page's PDF will print before it drops a level.
 *
 * MIRRORS the *_PDF_MAX_ROWS constants in azfApi/utils/reportPdf.js. The
 * drawer only warns with these; the renderer still enforces them.
 */
export const PAGE_ROW_CAPS = Object.freeze({
    laborManagement: 260,
    revenueOverview: 260,
    financialPnl: 420,
});

/**
 * Cumulative row counts by expansion level: index i is how many rows print
 * when every tier down to i is open.
 *
 * Levels are derived the way ReportMatrix derives them — an explicit `level`
 * wins, otherwise the parent chain decides — so the estimate matches what the
 * user is looking at.
 *
 * @param {Array} rows - ReportMatrix rows ({ id, parentId?, level?, … })
 * @returns {number[]|null} cumulative counts, or null when there is nothing to count
 */
export function rowCountsByLevel(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const byId = new Map(rows.map((row) => [row.id, row]));
    const levels = new Map();

    const levelOf = (row) => {
        if (levels.has(row.id)) return levels.get(row.id);
        let level = 0;
        let cursor = row;
        const seen = new Set([row.id]);
        for (;;) {
            if (Number.isInteger(cursor.level)) { level += cursor.level; break; }
            const parent = cursor.parentId != null ? byId.get(cursor.parentId) : null;
            // no parent, a parent outside the page, or a cycle: this is a root
            if (!parent) break;
            if (seen.has(parent.id)) { level = 0; break; }
            if (levels.has(parent.id)) { level += levels.get(parent.id) + 1; break; }
            seen.add(parent.id);
            cursor = parent;
            level += 1;
        }
        levels.set(row.id, Math.max(0, level));
        return levels.get(row.id);
    };

    const perLevel = [];
    rows.forEach((row) => {
        const level = levelOf(row);
        while (perLevel.length <= level) perLevel.push(0);
        perLevel[level] += 1;
    });

    let running = 0;
    return perLevel.map((count) => {
        running += count;
        return running;
    });
}
